import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Popconfirm, Table } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import { openNotificationSuccess, openNotificationError } from '../../../Components/Notification/Notification'

import AxiosCaller from '../../../authenticatie/AxiosCaller';
import { responsiveArray } from 'antd/es/_util/responsiveObserver';

const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.error('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingInlineEnd: 24 }} onClick={toggleEdit}>
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const AllergenenScreen = () => {
  const { get, put, del, post } = AxiosCaller();
  const [allergenen, setAllergenen] = useState([]);
  const [loading, setLoading] = useState(true);
  const tableRef = useRef(null);
  
  useEffect(() => {
    fetchAllergenen();
  }, []);

  const fetchAllergenen = async () => {
    try {
      const response = await get('/allergeen');
      const formattedData = response.data.map(allergeen => ({
        ...allergeen,
        key: allergeen._id
      }));
      setAllergenen(formattedData);
    } catch (error) {
      openNotificationError('Fout bij ophalen Allergenen', error)
      console.error('Error fetching allergenen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (key) => {
    try {
      await del(`/allergeen/${key}`);
      await fetchAllergenen();
      openNotificationSuccess("Allergeen is succesvol verwijderd!")
    } catch (error) {
      console.error('Error deleting allergeen:', error);
      openNotificationError("Fout bij verwijderen Allergeen", error)
    }
  };

  const handleSave = async (row) => {
    try {
      await put(`/allergeen/${row._id}`, { naam: row.naam });
      await fetchAllergenen();
      
    } catch (error) {
      console.error('Error updating allergeen:', error);
      openNotificationError('Fout bij aanpassen Allergeen', error)
    }
  };

  const handleAdd = async () => {
    try {
      // Create the new allergen with a specific name
      const newAllergene = { naam: 'Nieuw Allergeen' };
      const response = await post('/allergeen', newAllergene);
   
      // Add the new allergy at the top of the list instead of fetching everything again
      setAllergenen([{ ...response.data.savedAllergeen, key: response.data.savedAllergeen._id }, ...allergenen]);
      
      openNotificationSuccess("Nieuw allergeen is succesvol toegevoegd!");
    } catch (error) {
      console.error('Error adding allergeen:', error);
      openNotificationError('Fout bij toevoegen allergeen', error)
    }
  };
  const columns = [
    {
      title: 'Naam',
      dataIndex: 'naam',
      editable: true,
      sorter: (a, b) => a.naam.localeCompare(b.naam),
    },
    {
      title: 'Acties',
      dataIndex: 'operation',
      width: '15%',
      render: (_, record) => (
        <Popconfirm title="Weet je het zeker?" onConfirm={() => handleDelete(record._id)}>
          <Trash2 className='delete-button'/>
        </Popconfirm>
      ),
    },
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columnConfig = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div className='Screen-container'>
      <div className='space-between'>
        <h1 className='title'>Allergenen</h1>
        <Button onClick={handleAdd} type="primary" className='button'>
          <Plus /> Allergeen toevoegen
        </Button>
      </div>

      <Table
        ref={tableRef}
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={allergenen}
        columns={columnConfig}
        pagination={false}
        scroll={{ y: 0.5 * window.innerHeight }}
        loading={loading}
      />
    </div>
  );
};

export default AllergenenScreen;